/**
 * Auto Request - 主入口文件
 * 从 Swagger/Yapi JSON Schema 生成 TypeScript/JavaScript API 接口
 */
import { SwaggerJSON } from '@/define';
import Store from '@/store';
import { getTemplatePrefix } from '@/methods/base';
import { wrapperPreImportDefine } from '@/methods/generator-interface';
import { generateTemplates, generateJsDocTemplates, setIgnoreUrls } from '@/core/path-processor';
import { generatePathsDefine, generatePathsDefineNames } from '@/core/define-generator';
import { generatorSnapshots, AutoRequestOptions } from '@/core/snapshot';
import { generateFile } from '@/core/file-generator';
import { promptUser } from '@/core/prompt';
import { fetchSwagger, SwaggerSource, saveSwaggerBackup } from '@/core/swagger-fetcher';

// 导出公共 API
export { renderMethodArgs } from '@/methods/base';
export { wrapperMethodPreInterface } from '@/methods/generator-interface';
export { extractFunctionsFromCode, extractFunctionsFromFile } from '@/helpers/countFunctions';
export type { AutoRequestOptions } from '@/core/snapshot';
export type { SwaggerSource, SwaggerHttpConfig, SwaggerFetcherFunction } from '@/core/swagger-fetcher';
export { saveSwaggerBackup } from '@/core/swagger-fetcher';

// JSDoc 前缀
const jsdocsPrefix = `import {AxiosResponse, AxiosRequestConfig} from 'axios'\n`;

/**
 * 主函数：从 Swagger JSON 生成 API 接口代码
 * @param source - Swagger 数据源（JSON 字符串、HTTP 配置或自定义获取函数）
 * @param output - 输出目录
 * @param options - 配置选项
 * @returns Promise with write function and template
 */
export const autoRequest = async (
  source: SwaggerSource,
  output: string,
  options: AutoRequestOptions = {
    filename: '',
    isTypeScript: true,
    total: 0,
  }
) => {
  // 初始化 Store
  Store.isTypeScript = options.isTypeScript;
  Store.isJsDoc = options.jsdoc?.enabled || false;
  
  // 处理日志路径（向后兼容 loggerFileName）
  if (options.loggerPath && options.loggerFileName) {
    // 兼容旧写法：loggerPath 作为目录，loggerFileName 作为文件名
    Store.errorPath = options.loggerPath;
    Store.logFilename = options.loggerFileName;
  } else if (options.loggerPath) {
    // 新写法：loggerPath 直接是完整路径
    const lastSlashIndex = options.loggerPath.lastIndexOf('/');
    if (lastSlashIndex > -1) {
      Store.errorPath = options.loggerPath.substring(0, lastSlashIndex);
      Store.logFilename = options.loggerPath.substring(lastSlashIndex + 1);
    } else {
      Store.errorPath = '.';
      Store.logFilename = options.loggerPath;
    }
  }
  
  Store.filename = options.filename;
  
  // 设置忽略 URL 列表
  setIgnoreUrls(options.ignoreUrls || []);

  // 获取 Swagger JSON（支持多种数据源）
  const swaggerJson = await fetchSwagger(source);

  // 解析 Swagger JSON
  const sg = JSON.parse(swaggerJson) as SwaggerJSON;
  
  // **预检查阶段**：进行 Swagger 对比，但不影响 Store 数据
  if (options.swaggerBackupPath) {
    try {
      const fs = await import('fs');
      
      // 如果历史文件存在，进行对比
      if (fs.existsSync(options.swaggerBackupPath)) {
        console.log(`📊 检测到历史 Swagger 文件，开始预检查对比...\n`);
        
        const oldSwaggerJson = fs.readFileSync(options.swaggerBackupPath, 'utf-8');
        const { compareSwagger } = await import('@/core/swagger-comparator');
        const comparison = compareSwagger(oldSwaggerJson, swaggerJson);
        
        const totalChanges = comparison.added.length + comparison.removed.length + comparison.modified.length;
        
        if (totalChanges > 0) {
          console.log(`⚠️  检测到 Swagger 文档变更:`);
          console.log(`   - 新增接口: ${comparison.added.length} 个`);
          console.log(`   - 删除接口: ${comparison.removed.length} 个`);
          console.log(`   - 修改接口: ${comparison.modified.length} 个`);
          
          // 显示详细变更
          if (comparison.added.length > 0) {
            console.log(`\n   新增接口列表:`);
            comparison.added.slice(0, 5).forEach(ep => {
              console.log(`     [${ep.method}] ${ep.path} - ${ep.description || ep.summary || '无描述'}`);
            });
            if (comparison.added.length > 5) {
              console.log(`     ... 还有 ${comparison.added.length - 5} 个`);
            }
          }
          
          if (comparison.removed.length > 0) {
            console.log(`\n   删除接口列表:`);
            comparison.removed.slice(0, 5).forEach(ep => {
              console.log(`     [${ep.method}] ${ep.path} - ${ep.description || ep.summary || '无描述'}`);
            });
            if (comparison.removed.length > 5) {
              console.log(`     ... 还有 ${comparison.removed.length - 5} 个`);
            }
          }
          
          if (comparison.modified.length > 0) {
            console.log(`\n   修改接口列表:`);
            comparison.modified.slice(0, 5).forEach(({ endpoint, changes }) => {
              console.log(`     [${endpoint.method}] ${endpoint.path} - ${changes.join(', ')}`);
            });
            if (comparison.modified.length > 5) {
              console.log(`     ... 还有 ${comparison.modified.length - 5} 个`);
            }
          }
          
          console.log('');
          
          // 如果没有设置 skipPrompt，询问用户是否继续
          if (!options.skipPrompt) {
            const shouldContinue = await promptUser('检测到接口变更，是否继续生成代码？(y/n): ');
            if (!shouldContinue) {
              console.log('操作已取消。');
              return Promise.reject('操作已取消。');
            }
          }
        } else {
          console.log(`✅ Swagger 预检查: 无变化\n`);
        }
      } else {
        console.log(`💡 首次生成，将保存当前 Swagger 作为基准\n`);
      }
    } catch (error) {
      console.warn(`⚠️  Swagger 预检查失败: ${(error as Error).message}\n`);
    }
  } else {
    console.log('💡 提示：建议配置 swaggerBackupPath 参数来启用 Swagger 对比功能');
    console.log('   示例: swaggerBackupPath: "./swagger.json"\n');
  }

  // 生成文件头部
  const filePreFix =
    typeof options.renderHeaderTemplate === 'function' ? options.renderHeaderTemplate() : getTemplatePrefix();

  // 生成 TypeScript 类型定义引用
  const filePreDefineFix = Store.isTypeScript
    ? wrapperPreImportDefine(generatePathsDefineNames(sg.paths).join(','))
    : '';

  // 生成文件尾部
  const fileEndFix = typeof options.renderEndTemplate === 'function' ? options.renderEndTemplate() : '';

  // 生成 API 模板代码
  const content = generateTemplates(sg.paths, options.renderMethodCall);
  const defines = generatePathsDefine(sg.paths);
  const jsdocDefines = Store.isJsDoc
    ? generateJsDocTemplates(sg.paths, options.renderMethodJsDocCall).join('\n')
    : '';

  options.total = content.length;

  // 组合最终模板
  const template = filePreDefineFix + filePreFix + content.join('\n') + fileEndFix;

  // 生成快照并检测变更（传入 swaggerJson 用于 swagger 对比）
  await generatorSnapshots(options.snapshotsPath, output, options, template, swaggerJson);

  // 生成文件（JsDoc 文件的 prefix 和 endfix 从 options 获取）
  const result = generateFile(
    template, 
    defines, 
    { 
      content: jsdocDefines, 
      prefix: options.prefix || jsdocsPrefix,  // 优先使用自定义 prefix
      endifx: options.endfix || '' 
    }, 
    output, 
    options
  );
  
  // 生成完成后，保存 Swagger 备份（覆盖旧文件）
  if (options.swaggerBackupPath) {
    try {
      const savedPath = saveSwaggerBackup(swaggerJson, options.swaggerBackupPath);
      console.log(`✅ Swagger 文档已保存到: ${savedPath}`);
    } catch (error) {
      console.warn(`⚠️  Swagger 保存失败: ${(error as Error).message}`);
    }
  }
  
  return Promise.resolve(result);
};

/**
 * 遍历模板函数（简化版，不包含快照功能）
 * @param source - Swagger 数据源（JSON 字符串、HTTP 配置或自定义获取函数）
 * @param output - 输出目录
 * @param options - 配置选项
 * @returns Promise with write function
 */
export const eachRequestTemplate = async (source: SwaggerSource, output: string, options: AutoRequestOptions) => {
  // 获取 Swagger JSON（支持多种数据源）
  const swaggerJson = await fetchSwagger(source);

  const sg = JSON.parse(swaggerJson) as SwaggerJSON;
  const filePreFix =
    typeof options.renderHeaderTemplate === 'function' ? options.renderHeaderTemplate() : getTemplatePrefix();

  const content = generateTemplates(sg.paths, options.renderMethodCall);
  const defines = generatePathsDefine(sg.paths);

  const result = generateFile(filePreFix + content.join('\n'), defines, { content: '', prefix: '', endifx: '' }, output, options);
  
  // 生成完成后，保存 Swagger 备份（如果配置了备份路径）
  if (options.swaggerBackupPath) {
    try {
      const savedPath = saveSwaggerBackup(swaggerJson, options.swaggerBackupPath);
      console.log(`✅ Swagger 文档已保存到: ${savedPath}`);
    } catch (error) {
      console.warn(`⚠️  Swagger 保存失败: ${(error as Error).message}`);
    }
  }
  
  return Promise.resolve(result);
};

// 导出快照函数供独立使用
export { generatorSnapshots } from '@/core/snapshot';

// 导出文件生成函数供独立使用
export { generateFile } from '@/core/file-generator';
